function isLevelAllowed(label, level) {
    if (!label || level == null) return false;
    const normalizedLabel = label.toLowerCase();

    if (normalizedLabel === "all") return true;

    if (
        typeof level === "string" &&
        level.toLowerCase().includes("accenture leadership")
    ) {
        return !normalizedLabel.includes("below");
    }

    // const numericLevel = Number(level);
    const numericLevel = Number(String(level).replace(/\D/g, ""));
    if (Number.isNaN(numericLevel)) return false;

    if (normalizedLabel.includes("below")) {
        const maxLevel = Number(normalizedLabel.match(/\d+/)?.[0]);
        return numericLevel >= maxLevel;
    }

    if (normalizedLabel.includes("above")) {
        return numericLevel < 7;
    }
}

function includesValue(str, value) {
    if (!str) return false;
    if (str.trim().toLowerCase() === "all") return true;
    if (!value) return false;

    return str
        .toLowerCase()
        .split(",")
        .map(v => v.trim())
        .includes(value.toLowerCase());
}

export function isCourseEligible(course, userData, userLevel) {
    if (!course || !userData) return false;

    return (
        isLevelAllowed(course.level, userLevel) &&
        (
            (
                includesValue("Digital Customer", course.category) &&
                includesValue(course.sub_category, userData.capability)
            ) ||
            (
                includesValue("Industry", course.category) &&
                includesValue(course.sub_category, userData.industry)
            ) ||
            (
                includesValue("Technology", course.category) &&
                includesValue(course.sub_category, userData.category)
            ) ||
            (
                includesValue(
                    "GenAI for TDLC,Full-Stack GenAI,Specialization in Solutioning,AI for Solutioning,Agentic AI for Solutioning, Reinvention with Agentic AI",
                    course.category
                ) &&
                includesValue(course.capability, userData.capability) &&
                includesValue(course.industry, userData.industry) &&
                includesValue(course.role, userData.role)
            )
        )
    );
}

const FOUNDATION_MANDATORY_RULES = [
    { level: "L7&8", role: "Design,Solution Arch", digital: 4, industry: 1, tech: 1 },
    { level: "L7&8", role: "Technology,Delivery", digital: 1, industry: 1, tech: 1 },
    { level: "L9-11", role: "Design", digital: 1, industry: 1, tech: 0 },
    { level: "L9-11", role: "All", digital: 1, industry: 1, tech: 1 },

    { level: "L6& above", role: "All", digital: "All", industry: 1, tech: 1 },
];

const FLAGSHIP_MANDATORY_RULES = [
    { level: "L6& above", role: "Design", digital: 1, industry: 1, tech: 0 },
    { level: "L6& above", role: "All", digital: 1, industry: 1, tech: 1 },

    { level: "L7&8", role: "Design", digital: 1, industry: 1, tech: 0 },
    { level: "L7&8", role: "All", digital: 1, industry: 1, tech: 1 },

    { level: "L9-11", role: "All", digital: 0, industry: 0, tech: 0 },
];

function matchFoundationRule(userLevel, userRole) {
    if (!userLevel) return null;

    const numericLevel = Number(String(userLevel).replace(/\D/g, ""));
    const cleanRole = userRole?.toLowerCase().trim() || "";

    return FOUNDATION_MANDATORY_RULES.find(rule => {
        const ruleLevel = rule.level.toLowerCase();
        let levelMatch = false;

        if (ruleLevel.includes("& above")) {
            const min = Number(ruleLevel.match(/\d+/)?.[0]);
            levelMatch = numericLevel >= min;
        }
        else if (ruleLevel.includes("&")) {
            const levels = ruleLevel.match(/\d+/g)?.map(Number) || [];
            levelMatch = levels.includes(numericLevel);
        }
        else if (ruleLevel.includes("-")) {
            const [min, max] =
                ruleLevel.match(/\d+/g)?.map(Number) || [];
            levelMatch =
                numericLevel >= min &&
                numericLevel <= max;
        }

        const roleMatch =
            rule.role === "All" ||
            rule.role
                .toLowerCase()
                .split(",")
                .map(r => r.trim())
                .includes(cleanRole);

        return levelMatch && roleMatch;
    });
}

function matchFlagshipRule(userLevel, userRole) {
    if (!userLevel) return null;

    const numericLevel = Number(String(userLevel).replace(/\D/g, ""));
    const cleanRole = userRole?.toLowerCase().trim() || "";

    return FLAGSHIP_MANDATORY_RULES.find(rule => {

        const ruleLevel = rule.level.toLowerCase();

        let levelMatch = false;

        if (ruleLevel.includes("& above")) {
            const min = Number(ruleLevel.match(/\d+/)?.[0]);
            levelMatch = numericLevel >= min;
        }
        else if (ruleLevel.includes("&")) {
            const levels = ruleLevel.match(/\d+/g)?.map(Number) || [];
            levelMatch = levels.includes(numericLevel);
        }
        else if (ruleLevel.includes("-")) {
            const [min, max] =
                ruleLevel.match(/\d+/g)?.map(Number) || [];
            levelMatch =
                numericLevel >= min &&
                numericLevel <= max;
        }

        const roleMatch =
            rule.role === "All" ||
            rule.role
                .toLowerCase()
                .split(",")
                .map(r => r.trim())
                .includes(cleanRole);

        return levelMatch && roleMatch;
    });
}

function groupByCategoryAndModule(courses) {
    const grouped = {};

    courses.forEach(course => {
        const category = course.category || "Uncategorized";
        const moduleName = course.modules || "General";

        if (!grouped[category]) grouped[category] = {};
        if (!grouped[category][moduleName]) {
            grouped[category][moduleName] = {
                Mandatory: [],
                Optional: [],
            };
        }

        grouped[category][moduleName][course.courseType].push(course);
    });

    Object.keys(grouped).forEach(category => {
        const sorted = Object.keys(grouped[category])
            .sort((a, b) => {
                const aIndex =
                    courses.find(c => c.modules === a)?.indexing ?? 0;
                const bIndex =
                    courses.find(c => c.modules === b)?.indexing ?? 0;
                return aIndex - bIndex;
            })
            .reduce((acc, moduleName) => {
                acc[moduleName] = grouped[category][moduleName];
                return acc;
            }, {});

        grouped[category] = sorted;
    });

    return grouped;
}

export function getCoursesGroupedByCategoryAndModule({
    coursesByHub,
    userData,
    userLevel,
    hubName = "Foundation",
}) {
    if (!coursesByHub || !userData) return {};

    const hubCourses =
        coursesByHub.find(hub => hub.hubName === hubName)?.courses || [];

    let updatedCourses;

    if (hubName === "Foundation") {
        // No eligibility filtering for foundation
        updatedCourses = hubCourses.map(course => ({
            ...course,
            courseType: "Optional",
        }));
    } else {
        // Behaviour for other hubs
        updatedCourses = hubCourses.map(course => {
            const eligible = isCourseEligible(course, userData, userLevel);

            return {
                ...course,
                courseType:
                    eligible && course.courseType === "Mandatory"
                        ? "Mandatory"
                        : "Optional",
            };
        });
    }

    if (hubName === "Foundation") {
        const foundationRule = matchFoundationRule(userLevel, userData.role);
        const flagshipRule = matchFlagshipRule(userLevel, userData.role);

        const mandatoryIds = new Set();

        const categoryToRuleKey = {
            "digital customer": "digital",
            "industry": "industry",
            "technology": "tech",
        };

        const processModuleCourses = (courses, rule) => {
            if (!rule || !courses.length) return;

            const categories = ["Digital Customer", "Industry", "Technology"];

            categories.forEach(category => {
                const filtered = courses.filter(
                    c => c.category?.trim().toLowerCase() === category.toLowerCase()
                );

                if (!filtered.length) return;

                // Map category to rule key
                const key = categoryToRuleKey[category.toLowerCase()];
                const count = rule[key];

                if (!count || count === 0) return; // skip if no mandatory required

                if (count === "All") {
                    filtered.forEach(c => mandatoryIds.add(c.id));
                    return;
                }

                if (count === 1 && userData) {
                    let matched = null;

                    if (category === "Digital Customer") {
                        matched = filtered.find(c => includesValue(c.capability, userData.capability));
                    }
                    if (category === "Industry") {
                        matched = filtered.find(c => includesValue(c.industry, userData.industry));
                    }
                    if (category === "Technology") {
                        matched = filtered.find(c => includesValue(c.sub_category, userData.category));
                    }

                    if (matched) mandatoryIds.add(matched.id);
                    else mandatoryIds.add(filtered[0].id);
                    return;
                }

                // Case when count > 1
                filtered.slice(0, count).forEach(c => mandatoryIds.add(c.id));
            });
        };

        // Process Foundation and Flagship modules
        const foundationModuleCourses = updatedCourses.filter(
            c => c.modules?.trim().toLowerCase() === "foundation"
        );
        processModuleCourses(foundationModuleCourses, foundationRule);

        // const flagshipModuleCourses = updatedCourses.filter(
        //     c => c.modules?.trim().toLowerCase() === "flagship"
        // );
        // processModuleCourses(flagshipModuleCourses, flagshipRule);

        // Update courseType for all courses
        updatedCourses = updatedCourses.map(c => {
            const isFlagship =
                c.modules?.trim().toLowerCase() === "flagship";

            if (isFlagship) {
                // Always force Flagship to Optional
                return {
                    ...c,
                    courseType: "Optional",
                };
            }

            return {
                ...c,
                courseType: mandatoryIds.has(c.id) ? "Mandatory" : "Optional",
            };
        });
    }

    return groupByCategoryAndModule(updatedCourses);
}

export function isFoundationUnlocked({
    coursesByHub,
    userData,
    userLevel,
}) {
    if (!coursesByHub || !userData) return false;

    const grouped = getCoursesGroupedByCategoryAndModule({
        coursesByHub,
        userData,
        userLevel,
        hubName: "Foundation",
    });

    const mandatoryCourses = Object.values(grouped)
        .flatMap(modules =>
            Object.values(modules).flatMap(
                m => m.Mandatory || []
            )
        );

    if (!mandatoryCourses.length) return true;

    return mandatoryCourses.every(
        course => Boolean(course.completionDate)
    );
}

export function getCoursesGroupedCourses({
    coursesByHub,
    userData,
    userLevel,
    hubName = "Foundation",
}) {
    if (!coursesByHub || !userData) return {};

    const hubCourses =
        coursesByHub.find(hub => hub.hubName === hubName)?.courses || [];

    let updatedCourses;

    if (hubName === "Foundation") {
        // No eligibility filtering for foundation
        updatedCourses = hubCourses.map(course => ({
            ...course,
            courseType: "Optional",
        }));
    } else {
        // Behaviour for other hubs
        updatedCourses = hubCourses.map(course => {
            const eligible = isCourseEligible(course, userData, userLevel);

            return {
                ...course,
                courseType:
                    eligible && course.courseType === "Mandatory"
                        ? "Mandatory"
                        : "Optional",
            };
        });
    }

    if (hubName === "Foundation") {
        const foundationRule = matchFoundationRule(userLevel, userData.role);
        const flagshipRule = matchFlagshipRule(userLevel, userData.role);

        const mandatoryIds = new Set();

        const categoryToRuleKey = {
            "digital customer": "digital",
            "industry": "industry",
            "technology": "tech",
        };

        const processModuleCourses = (courses, rule) => {
            if (!rule || !courses.length) return;

            const categories = ["Digital Customer", "Industry", "Technology"];

            categories.forEach(category => {
                const filtered = courses.filter(
                    c => c.category?.trim().toLowerCase() === category.toLowerCase()
                );

                if (!filtered.length) return;

                // Map category to rule key
                const key = categoryToRuleKey[category.toLowerCase()];
                const count = rule[key];

                if (!count || count === 0) return; // skip if no mandatory required

                if (count === "All") {
                    filtered.forEach(c => mandatoryIds.add(c.id));
                    return;
                }

                if (count === 1 && userData) {
                    let matched = null;

                    if (category === "Digital Customer") {
                        matched = filtered.find(c => includesValue(c.capability, userData.capability));
                    }
                    if (category === "Industry") {
                        matched = filtered.find(c => includesValue(c.industry, userData.industry));
                    }
                    if (category === "Technology") {
                        matched = filtered.find(c => includesValue(c.sub_category, userData.category));
                    }

                    if (matched) mandatoryIds.add(matched.id);
                    else mandatoryIds.add(filtered[0].id);
                    return;
                }

                // Case when count > 1
                filtered.slice(0, count).forEach(c => mandatoryIds.add(c.id));
            });
        };

        // Process Foundation and Flagship modules
        const foundationModuleCourses = updatedCourses.filter(
            c => c.modules?.trim().toLowerCase() === "foundation"
        );
        processModuleCourses(foundationModuleCourses, foundationRule);

        // const flagshipModuleCourses = updatedCourses.filter(
        //     c => c.modules?.trim().toLowerCase() === "flagship"
        // );
        // processModuleCourses(flagshipModuleCourses, flagshipRule);

        // Update courseType for all courses
        updatedCourses = updatedCourses.map(c => {
            const isFlagship =
                c.modules?.trim().toLowerCase() === "flagship";

            if (isFlagship) {
                // Always force Flagship to Optional
                return {
                    ...c,
                    courseType: "Optional",
                };
            }

            return {
                ...c,
                courseType: mandatoryIds.has(c.id) ? "Mandatory" : "Optional",
            };
        });
    }

    return updatedCourses;
}